import { createContext, useContext, type ReactNode } from 'react';
import { Avatar, IconButton, IconLink, iconBack, iconBell, iconClose } from '@sauda/ui/kit';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { cx } from '@sauda/ui/kit';
import { useAuth } from '../auth/AuthContext.js';
import { BillingBanner } from './Banners.js';
import { initials } from '../lib/initials.js';
import { usePane } from './panes.js';

export interface Chrome {
  openAccount: () => void;
  unread: number;
  /** Writes are switched off for this agency (read only, deletion). The reason is shown beside the action. */
  readOnly: boolean;
}

export const ChromeContext = createContext<Chrome>({
  openAccount: () => undefined,
  unread: 0,
  readOnly: false,
});

export function useChrome(): Chrome {
  return useContext(ChromeContext);
}

/** The bell (a dot when something waits) and the account button with the person's initials. */
export function TopActions() {
  const { t } = useTranslation();
  const chrome = useChrome();
  const auth = useAuth();
  return (
    <>
      <IconLink
        as={Link}
        to="/notifications"
        icon={iconBell}
        className="screen__bell"
        label={
          chrome.unread > 0 ? t('shell.bellUnread', { count: chrome.unread }) : t('shell.bell')
        }
      >
        {chrome.unread > 0 ? <span className="k-dot" aria-hidden="true" /> : null}
      </IconLink>
      <button type="button" className="screen__me" onClick={chrome.openAccount}>
        <Avatar size="sm" decorative={false} initials={initials(auth.name)} />
        <span className="k-sr-only">{t('shell.account')}</span>
      </button>
    </>
  );
}

export interface ScreenProps {
  title: string;
  /** Small line above the title, for example the agency and branch. */
  eyebrow?: string | undefined;
  /** A tab root has a large title; a detail screen has a small one and a back button. */
  large?: boolean;
  /** Lay the cards out in columns on a wide window (dashboards). */
  wide?: boolean;
  /** A path to go back to, a function, or `true` for the previous screen. */
  back?: string | (() => void) | true;
  /** Draw the back button as a close cross, for the first step of a flow. */
  backIsClose?: boolean;
  /** Replaces the bell and account button, for a screen with its own menu. */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** A screen of the broker app: the top bar with the bell and the account button, then the content. */
export function Screen({
  title,
  eyebrow,
  large = false,
  wide = false,
  back,
  backIsClose = false,
  actions,
  children,
  className,
}: ScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // In the detail pane of a two pane layout the list is right there: no back button, no bell, but the
  // screen's own actions (edit, more) stay.
  const inDetail = usePane() === 'detail';
  const goBack = () => {
    if (typeof back === 'function') back();
    else if (typeof back === 'string') void navigate(back);
    else void navigate(-1);
  };
  return (
    <div className={cx('screen', inDetail && 'screen--detail', wide && 'screen--cols', className)}>
      <header className="screen__head">
        <div className="screen__row">
          {back === undefined || inDetail ? null : (
            <IconButton
              icon={backIsClose ? iconClose : iconBack}
              label={t(backIsClose ? 'app.close' : 'app.back')}
              onClick={goBack}
            />
          )}
          <div className="screen__titles k-grow">
            {eyebrow === undefined || eyebrow === '' ? null : (
              <p className="screen__eyebrow k-t-label-sm k-muted">{eyebrow}</p>
            )}
            <h1 className={large && !inDetail ? 'k-t-headline-lg' : 'k-t-title'}>{title}</h1>
          </div>
          {inDetail ? (actions ?? null) : (actions ?? <TopActions />)}
        </div>
      </header>
      <div className="screen__body">
        {inDetail ? null : <BillingBanner />}
        {children}
      </div>
    </div>
  );
}
